import type { LangDict } from "./types";

// Shared strings: buttons, bottom nav, the registration/login wizard, and
// the parts of Settings that aren't role-specific (Appearance, Language,
// Log out). Owner/tenant-only screens live in owner.ts / tenant.ts.
export const core: LangDict = {
  en: {
    common: {
      save: "Save changes",
      saving: "Saving…",
      cancel: "Cancel",
      remove: "Remove",
      continue: "Continue",
      somethingWrong: "Something went wrong",
      logOut: "Log out",
      welcomeBackGreet: "Welcome back,",
      hiGreet: "Hi,",
      statusOpen: "Open",
      statusInProgress: "In progress",
      statusResolved: "Resolved",
    },
    foundingOffer: {
      title: "Founding Owner offer: Pro free for {months} months",
      body: "No card needed. {remaining} of {limit} spots left.",
      titlePremium: "Founding Owner offer: Premium free for {months} months",
      bodyPremium: "Only the first {premiumLimit} owners: {remaining} Premium spots left. After that Pro is free for the first {limit}. No card needed.",
      noticeTitle: "You're Founding Owner #{spot}",
      noticeBody: "{plan} is free for you until {date} ({days} days left). Afterwards keep {plan} for ₹{price}/month, or stay on the free plan.",
      doneTitle: "You're Founding Owner #{spot}!",
      doneBody: "{plan} is free for you until {date}. No card needed.",
    },
    confirmLocation: {
      title: "Confirm the location of {name}",
      body: "We placed this pin from a map. Tap the button while you are at the property so tenants find the exact spot.",
      btn: "I'm at the property: confirm",
      working: "Getting your location…",
      doneToast: "Location confirmed",
      denied: "We could not get your location. Allow location access and try again.",
      noGps: "This device cannot share its location.",
      approximate: "Approximate location",
    },
    verifyEmail: {
      bannerTitle: "Verify your email",
      bannerBody: "Confirm {email} to unlock everything and keep your account safe.",
      verifyBtn: "Verify",
      sheetTitle: "Verify your email",
      sendIntro: "We'll send a 6-digit code to {email}.",
      sendBtn: "Send code",
      sending: "Sending…",
      sentTo: "We sent a 6-digit code to {email}. It expires in 10 minutes.",
      confirmBtn: "Verify email",
      checking: "Checking…",
      resendBtn: "Send a new code",
      resendIn: "Send a new code in {seconds}s",
      later: "Not now",
      verifiedToast: "Email verified",
      signupEyebrow: "Step 1",
      signupTitle: "Check your email",
      changeEmail: "Use a different email",
    },
    appUpdate: {
      bannerTitle: "A new version is available",
      bannerBody: "Update ToLetBook to get the latest features and fixes.",
      updateBtn: "Update now",
      dismissAria: "Dismiss",
      requiredTitle: "Please update ToLetBook",
      requiredBody: "This version is no longer supported. Update from Google Play to keep using the app.",
    },
    rateApp: {
      menuLabel: "Rate ToLetBook",
      title: "Enjoying ToLetBook?",
      subtitle: "Tap a star to rate your experience.",
      commentLikeLabel: "What do you like most? (optional)",
      commentFixLabel: "What should we improve? (optional)",
      commentPlaceholder: "Write a few words",
      submitBtn: "Submit",
      notNow: "Not now",
      thanksHappyTitle: "Thank you!",
      thanksHappyBody: "We're glad you like it. Would you share your rating on Google Play too? It helps other owners and tenants find us.",
      rateOnPlayBtn: "Rate on Google Play",
      openPlayPage: "Open the Google Play page",
      maybeLater: "Maybe later",
      thanksSadTitle: "Thanks for telling us",
      thanksSadBody: "We read every comment and we'll work on making ToLetBook better.",
      close: "Close",
    },
    planChanged: {
      title: "You're on {plan} now!",
      body: "Your payment went through and your plan has been upgraded to {plan}. Enjoy the new benefits.",
      okBtn: "Awesome",
    },
    requests: {
      categoryLabel: "Category (optional)",
      categoryPlumbing: "Plumbing",
      categoryElectrical: "Electrical",
      categoryAppliance: "Appliance",
      categoryFurniture: "Furniture",
      categoryCleaning: "Cleaning",
      categoryPestControl: "Pest Control",
      categoryOther: "Other",
      urgentCheckboxLabel: "This is urgent",
      urgentBadge: "Urgent",
      photoLabel: "Add a photo (optional)",
      addPhotoBtn: "Add photo",
      removePhotoAria: "Remove photo",
      updatesHeading: "Updates",
      noUpdatesYet: "No updates yet.",
      addCommentPlaceholder: "Add a note…",
      postCommentBtn: "Post",
      postingCommentBtn: "Posting…",
      callBtn: "Call",
      whatsappBtn: "WhatsApp",
    },
    nav: {
      home: "Home",
      listings: "Listings",
      notebook: "Notebook",
      requests: "Requests",
      settings: "Settings",
      browse: "Browse Properties",
      payments: "Payments",
      ownerDashboard: "OWNER DASHBOARD",
      tenantDashboard: "TENANT DASHBOARD",
    },
    charges: {
      rent: "Rent",
      electricity: "Electricity",
      lateFee: "Late fee",
      other: "Other",
      total: "Total",
      remove: "Remove",
    },
    auth: {
      welcomeEyebrow: "Welcome",
      welcomeTitle: "Welcome to ToLetBook",
      welcomeSub: "Log in to your account, or create a new one.",
      logIn: "Log In",
      createAccount: "Create an Account",
      loginTitle: "Welcome back",
      loginSub: "Log in to continue to your dashboard.",
      registerTitle: "Create your account",
      registerSub: "List a property or find your next home — no brokers, no hassle.",
      passwordHint: "At least 6 characters.",
      showPassword: "Show password",
      hidePassword: "Hide password",
      referralCodeLabel: "Referral code (optional)",
      referralCodePlaceholder: "Enter code if you have one",
      agreeTermsText: "I agree to ToLetBook's Terms & Conditions and Privacy Policy",
      viewTermsLink: "View Terms & Conditions",
      viewPrivacyLink: "View Privacy Policy",
      hideTermsLink: "Hide Terms & Conditions",
      errMustAgreeTerms: "Please agree to the Terms & Conditions to continue",
      loginBusy: "Logging in…",
      emailLabel: "Email",
      emailPlaceholder: "you@example.com",
      passwordLabel: "Password",
      passwordPlaceholder: "Your password",
      errEmailPassword: "Enter your email and password",
      getStartedEyebrow: "Get Started",
      roleTitle: "Tell us who you are",
      roleSub: "So we can set up the right dashboard.",
      roleOwnerTitle: "I'm a Property / PG Owner",
      roleOwnerSub: "List properties, manage tenants, track rent",
      roleTenantTitle: "I'm a Tenant",
      roleTenantSub: "Browse listings, pay rent, raise requests",
      step1Eyebrow: "Step 1",
      credentialsTitle: "Create your login",
      newPasswordPlaceholder: "At least 6 characters",
      phoneOptionalLabel: "Mobile number (optional)",
      phoneLabel: "Mobile number",
      phonePlaceholder: "10-digit mobile number",
      errValidEmail: "Enter a valid email address",
      errEmailTaken: "An account already exists for this email — try logging in instead",
      errValidPhone: "Enter a valid 10-digit mobile number",
      errPasswordLength: "Password must be at least 6 characters",
      errValidPhoneOptional: "Enter a valid 10-digit mobile number, or leave it blank",
      step2Eyebrow: "Step 2",
      profileTitle: "A little about you",
      fullNameLabel: "Full name",
      fullNamePlaceholder: "Your full name",
      profileBusy: "Please wait…",
      errName: "Enter your name",
      step3Eyebrow: "Step 3",
      propertyTypeTitle: "What do you manage?",
      propertyTypeSub: "This decides which dashboard views you'll see — you can change it later.",
      typeIndependentTitle: "Independent properties",
      typeIndependentSub: "Full houses, flats, villas",
      typePgTitle: "PG / hostel",
      typePgSub: "Shared rooms billed per bed",
      typeBothTitle: "Both",
      typeBothSub: "A mix of independent units and PG rooms",
      almostDoneEyebrow: "Almost done",
      cityTitle: "Where do you operate?",
      citySub: "We'll use this to show your listings to nearby renters first.",
      cityLabel: "City",
      cityPlaceholder: "e.g. Mumbai",
      bankDetailsTitle: "How should we pay you?",
      bankDetailsSub: "Add your bank account so rent and deposit payments can be sent to you directly.",
      payoutMethodUpi: "UPI ID",
      payoutMethodBank: "Bank account",
      upiIdLabel: "UPI ID",
      upiIdPlaceholder: "e.g. name@okhdfcbank",
      errValidUpi: "Enter a valid UPI ID",
      bankAccountHolderLabel: "Account holder name",
      bankAccountHolderPlaceholder: "As per bank records",
      bankAccountNumberLabel: "Account number",
      bankAccountNumberPlaceholder: "Bank account number",
      bankIfscLabel: "IFSC code",
      bankIfscPlaceholder: "e.g. HDFC0001234",
      bankDetailsHint: "Tenants will pay rent and deposit straight to this — you can edit it anytime from Settings.",
      errUpiRequired: "Add a UPI ID so tenants can pay you directly",
      errBankDetailsRequired: "Add your account holder name, account number and IFSC code",
      errValidIfsc: "Enter a valid IFSC code",
      finishBusy: "Setting up…",
      finish: "Finish",
      doneTitle: "You're all set!",
      doneSub: "Your dashboard is ready.",
      goToDashboard: "Go to Dashboard",
      welcomeBackToast: "Welcome back, {name}!",
      welcomeNewToast: "Welcome to ToLetBook, {name}!",
      orDivider: "or",
      googleNoAccountToast: "No ToLetBook account found for that Google email — let's get you set up.",
      googleNoAccountNotice: "No ToLetBook account found for {email}. Create one below: choose who you are and add your mobile number.",
      continueWithGoogle: "Continue with Google",
      googleSignInFailedToast: "Google sign-in didn't go through — try again, or use email and password.",
      forgotPasswordLink: "Forgot password?",
      forgotPasswordEyebrow: "Reset Password",
      forgotPasswordTitle: "Forgot your password?",
      forgotPasswordSub: "Enter your email and we'll send you a 6-digit code to reset it.",
      forgotPasswordSubmit: "Send Code",
      forgotPasswordBusy: "Sending…",
      forgotPasswordSentTitle: "Check your email",
      forgotPasswordSentSub: "If an account exists for that email, we've sent a 6-digit code to reset your password.",
      forgotPasswordCodeLabel: "6-digit code",
      forgotPasswordCodePlaceholder: "123456",
      forgotPasswordResendLink: "Didn't get it? Resend code",
      forgotPasswordResendToast: "Code resent",
      forgotPasswordChangeEmailLink: "Wrong email? Go back",
      resetPasswordEyebrow: "Reset Password",
      resetPasswordTitle: "Choose a new password",
      resetPasswordSub: "This code is valid for 10 minutes.",
      newPasswordLabel: "New password",
      confirmPasswordLabel: "Confirm new password",
      errPasswordMismatch: "Passwords don't match",
      errCodeLength: "Enter the 6-digit code",
      resetPasswordSubmit: "Reset Password",
      resetPasswordBusy: "Resetting…",
      resetPasswordDoneTitle: "Password reset",
      resetPasswordDoneSub: "You can now log in with your new password.",
    },
    settings: {
      title: "Settings",
      generalSection: "General",
      supportSection: "Support",
      accountSection: "Account",
      profileMenu: "Profile",
      reportsMenu: "Reports",
      plansMenu: "Plans & billing",
      referMenu: "Refer & Earn",
      broadcastMenu: "WhatsApp Broadcast",
      savedSearchesMenu: "Saved Search Alerts",
      appearance: "Appearance",
      language: "Language",
      dark: "Dark",
      light: "Light",
      termsTitle: "Terms & Conditions",
      privacyTitle: "Privacy Policy",
      bankDetailsMenu: "My Bank Details",
      bankDetailsHint: "Tenants pay rent and deposit straight to this UPI ID or bank account — shown only to your own tenants, never made public.",
      bankDetailsSaved: "Bank details saved",
      contactUsMenu: "Contact Us",
      deleteAccountMenu: "Delete Account",
      deletingBusy: "Sending request…",
      confirmDeleteAccount: "Request account deletion? Our support team reviews every request and completes it within 30 days — you'll keep access until then. This can't be undone once processed.",
      deletionRequestedToast: "Deletion request sent — our team will process it within 30 days",
      deletionRequestedLabel: "Account deletion requested — in progress",
      emailUsedToLogIn: "Email (used to log in)",
      name: "Name",
      phone: "Phone number",
      phonePlaceholder: "10-digit mobile number",
      city: "City",
      profileUpdated: "Profile updated",
      demoNotice: "Accounts here are for demo purposes only; use a throwaway email and password.",
      verificationTitle: "Identity Verification",
      verificationHint: "Upload a government ID (front and back) to get a Verified badge on your profile and listings.",
      verificationSubmit: "Submit for verification",
      verificationSubmitted: "Submitted for verification",
      errIdPhotosRequired: "Add both the front and back of your ID",
      verificationVerified: "You're verified",
      verificationPending: "Verification pending",
      verificationPendingSub: "We'll notify you once it's reviewed — usually within a day or two.",
      verificationRejected: "Verification wasn't approved",
      changePasswordTitle: "Change Password",
      currentPasswordLabel: "Current password",
      errCurrentPasswordRequired: "Enter your current password",
      passwordChanged: "Password changed",
      changePasswordButton: "Update Password",
    },
    notifications: {
      title: "Notifications",
      enabledToast: "Notifications turned on",
      disabledToast: "Notifications turned off",
      couldNotEnableToast: "Couldn't turn on notifications",
      notConfigured: "Notifications aren't set up on this server yet",
      deniedHint: "Notifications are blocked for this app in your device settings.",
      markAllRead: "Mark all read",
      emptyState: "No notifications yet.",
    },
    refer: {
      title: "Refer & Earn",
      heroTitle: "Give 1 month, Get 1 month",
      heroSub: "Invite a friend to ToLetBook. When they download the app and get started — list their first property as an owner, or unlock their first contact as a tenant — you both earn 1 month of Pro free. If you're not already on a paid plan, activate it anytime from this screen — it only takes a minute and nothing is charged until your free month ends.",
      yourCodeLabel: "Your referral code",
      shareButton: "Share invite",
      copiedToast: "Referral link copied",
      statsReferred: "{count} invited",
      statsRewarded: "{count} rewarded",
      statsRemaining: "{count} free months left",
      howItWorksTitle: "How it works",
      step1: "Share your code or link with a friend",
      step2: "They download ToLetBook and sign up",
      step3: "They list their first property as an owner, or unlock their first contact as a tenant — you both earn 1 month of Pro free, ready to activate",
      historyTitle: "Your referrals",
      emptyState: "No referrals yet — share your code to get started",
      statusPending: "Waiting for their first listing",
      statusRewarded: "Rewarded — 1 month free",
      statusCapped: "They got rewarded — you'd hit your limit",
      shareMessage: "Join me on ToLetBook! Use my referral code {code} when you sign up — we'll both get 1 month of Pro free.",
      claimBannerTitle: "{count} free month(s) of Pro waiting!",
      claimBannerSub: "Activate them to start using Pro right away. Since Pro is a recurring plan, activating sets up autopay for when your free time runs out — you'll see exactly what that means before anything is confirmed.",
      claimButton: "Activate free Pro",
      consentBullet1: "This sets up autopay for ToLetBook Pro (₹99/month). Nothing is charged today.",
      consentBullet2: "Your {count} free month(s) start right away.",
      consentBullet3: "After your free month(s) end, ₹99 will be auto-debited every month unless you cancel.",
      consentBullet4: "You can cancel anytime from Plans — cancel before your free time ends and you won't be charged.",
      consentCheckbox: "I understand and agree to set up autopay for ToLetBook Pro",
      consentConfirmButton: "Set up autopay & activate",
      consentCancelButton: "Not now",
      claimSuccessToast: "Pro activated — free until {date}",
      claimFailedToast: "Couldn't activate your reward — try again",
      noChargeNote: "Payments aren't set up on this server yet — you'll be able to activate this once they are.",
      mandateDescription: "ToLetBook Pro — free until {date}, then ₹99/month",
    },
    ownerTerms: {
      intro: "By using ToLetBook you agree to these terms.",
      bullet1: "Rent and deposit payments made through ToLetBook are processed via Razorpay. ToLetBook is not a bank and doesn't guarantee outcomes beyond what Razorpay confirms.",
      bullet2: "You're responsible for the accuracy of the property, tenant, and payment information you enter.",
      bullet3: "Your data is stored to operate the service and isn't sold to third parties.",
      bullet4: "Figures in Reports and Notebook are computed directly from data you enter — not independently audited or verified.",
      bullet5: "Map and location features are provided by Google Maps, subject to Google's own terms.",
    },
    tenantTerms: {
      intro: "By using ToLetBook you agree to these terms.",
      bullet1: "Rent and deposit payments made through ToLetBook are processed via Razorpay. ToLetBook is not a bank and doesn't guarantee outcomes beyond what Razorpay confirms.",
      bullet2: "You're responsible for the accuracy of the information you provide.",
      bullet3: "Your data is stored to operate the service and isn't sold to third parties.",
      bullet4: "Figures shown are computed directly from data entered by you or your owner — not independently audited or verified.",
      bullet5: "Map and location features are provided by Google Maps, subject to Google's own terms.",
    },
  },
  hi: {
    common: {
      save: "बदलाव सेव करें",
      saving: "सेव हो रहा है…",
      cancel: "रद्द करें",
      remove: "हटाएं",
      continue: "आगे बढ़ें",
      somethingWrong: "कुछ गड़बड़ हो गई",
      logOut: "लॉग आउट",
      welcomeBackGreet: "वापसी पर स्वागत है,",
      hiGreet: "नमस्ते,",
      statusOpen: "खुला",
      statusInProgress: "प्रगति पर",
      statusResolved: "हल हो गया",
    },
    requests: {
      categoryLabel: "श्रेणी (वैकल्पिक)",
      categoryPlumbing: "प्लंबिंग",
      categoryElectrical: "इलेक्ट्रिकल",
      categoryAppliance: "उपकरण",
      categoryFurniture: "फर्नीचर",
      categoryCleaning: "सफाई",
      categoryPestControl: "पेस्ट कंट्रोल",
      categoryOther: "अन्य",
      urgentCheckboxLabel: "यह अत्यावश्यक है",
      urgentBadge: "अत्यावश्यक",
      photoLabel: "फोटो जोड़ें (वैकल्पिक)",
      addPhotoBtn: "फोटो जोड़ें",
      removePhotoAria: "फोटो हटाएं",
      updatesHeading: "अपडेट्स",
      noUpdatesYet: "अभी तक कोई अपडेट नहीं।",
      addCommentPlaceholder: "एक नोट जोड़ें…",
      postCommentBtn: "पोस्ट करें",
      postingCommentBtn: "पोस्ट हो रहा है…",
      callBtn: "कॉल करें",
      whatsappBtn: "व्हाट्सएप",
    },
    nav: {
      home: "होम",
      listings: "लिस्टिंग",
      notebook: "नोटबुक",
      requests: "रिक्वेस्ट",
      settings: "सेटिंग्स",
      browse: "प्रॉपर्टी देखें",
      payments: "भुगतान",
      ownerDashboard: "मालिक डैशबोर्ड",
      tenantDashboard: "किराएदार डैशबोर्ड",
    },
    auth: {
      welcomeEyebrow: "स्वागत है",
      welcomeTitle: "ToLetBook में आपका स्वागत है",
      welcomeSub: "अपने अकाउंट में लॉग इन करें, या नया अकाउंट बनाएं।",
      logIn: "लॉग इन करें",
      createAccount: "अकाउंट बनाएं",
      loginTitle: "वापसी पर स्वागत है",
      loginSub: "अपने डैशबोर्ड पर जाने के लिए लॉग इन करें।",
      registerTitle: "अपना अकाउंट बनाएं",
      registerSub: "प्रॉपर्टी लिस्ट करें या अपना अगला घर खोजें — बिना ब्रोकर, बिना झंझट।",
      passwordHint: "कम से कम 6 अक्षर।",
      referralCodeLabel: "रेफरल कोड (वैकल्पिक)",
      referralCodePlaceholder: "अगर आपके पास कोड है तो डालें",
      agreeTermsText: "मैं ToLetBook के नियम व शर्तें और प्राइवेसी पॉलिसी से सहमत हूं",
      viewTermsLink: "नियम व शर्तें देखें",
      viewPrivacyLink: "गोपनीयता नीति देखें",
      hideTermsLink: "नियम व शर्तें छिपाएं",
      errMustAgreeTerms: "आगे बढ़ने के लिए कृपया नियम व शर्तों से सहमत हों",
      loginBusy: "लॉग इन हो रहा है…",
      emailLabel: "ईमेल",
      emailPlaceholder: "you@example.com",
      passwordLabel: "पासवर्ड",
      passwordPlaceholder: "आपका पासवर्ड",
      errEmailPassword: "अपना ईमेल और पासवर्ड डालें",
      getStartedEyebrow: "शुरू करें",
      roleTitle: "आप कौन हैं, बताएं",
      roleSub: "ताकि हम आपके लिए सही डैशबोर्ड तैयार कर सकें।",
      roleOwnerTitle: "मैं प्रॉपर्टी / पीजी मालिक हूं",
      roleOwnerSub: "प्रॉपर्टी लिस्ट करें, किराएदार प्रबंधित करें, किराया ट्रैक करें",
      roleTenantTitle: "मैं किराएदार हूं",
      roleTenantSub: "लिस्टिंग देखें, किराया भरें, रिक्वेस्ट भेजें",
      step1Eyebrow: "चरण 1",
      credentialsTitle: "अपना लॉगिन बनाएं",
      newPasswordPlaceholder: "कम से कम 6 अक्षर",
      phoneOptionalLabel: "मोबाइल नंबर (वैकल्पिक)",
      phonePlaceholder: "10 अंकों का मोबाइल नंबर",
      errValidEmail: "एक सही ईमेल पता डालें",
      errPasswordLength: "पासवर्ड कम से कम 6 अक्षर का होना चाहिए",
      errValidPhoneOptional: "10 अंकों का सही मोबाइल नंबर डालें, या इसे खाली छोड़ दें",
      step2Eyebrow: "चरण 2",
      profileTitle: "अपने बारे में थोड़ा बताएं",
      fullNameLabel: "पूरा नाम",
      fullNamePlaceholder: "आपका पूरा नाम",
      profileBusy: "कृपया प्रतीक्षा करें…",
      errName: "अपना नाम डालें",
      step3Eyebrow: "चरण 3",
      propertyTypeTitle: "आप क्या मैनेज करते हैं?",
      propertyTypeSub: "इससे तय होगा कि आपको कौन-सा डैशबोर्ड दिखेगा — इसे बाद में बदला जा सकता है।",
      typeIndependentTitle: "इंडिपेंडेंट प्रॉपर्टी",
      typeIndependentSub: "पूरे घर, फ्लैट, विला",
      typePgTitle: "पीजी / हॉस्टल",
      typePgSub: "बेड के हिसाब से किराए वाले शेयर्ड कमरे",
      typeBothTitle: "दोनों",
      typeBothSub: "इंडिपेंडेंट यूनिट्स और पीजी कमरों का मिश्रण",
      almostDoneEyebrow: "लगभग हो गया",
      cityTitle: "आप कहां काम करते हैं?",
      citySub: "इससे हम आपकी लिस्टिंग पास के किराएदारों को सबसे पहले दिखाएंगे।",
      cityLabel: "शहर",
      cityPlaceholder: "जैसे मुंबई",
      bankDetailsTitle: "हम आपको भुगतान कैसे करें?",
      bankDetailsSub: "अपना बैंक अकाउंट जोड़ें ताकि किराया और डिपॉज़िट सीधे आपको भेजे जा सकें।",
      payoutMethodUpi: "UPI ID",
      payoutMethodBank: "बैंक अकाउंट",
      upiIdLabel: "UPI ID",
      upiIdPlaceholder: "जैसे name@okhdfcbank",
      errValidUpi: "सही UPI ID डालें",
      bankAccountHolderLabel: "खाताधारक का नाम",
      bankAccountHolderPlaceholder: "बैंक रिकॉर्ड के अनुसार",
      bankAccountNumberLabel: "खाता नंबर",
      bankAccountNumberPlaceholder: "बैंक खाता नंबर",
      bankIfscLabel: "IFSC कोड",
      bankIfscPlaceholder: "जैसे HDFC0001234",
      bankDetailsHint: "आप इसे कभी भी सेटिंग्स से जोड़ या बदल सकते हैं।",
      skipForNowLink: "अभी छोड़ें",
      errValidIfsc: "सही IFSC कोड डालें",
      finishBusy: "तैयार किया जा रहा है…",
      finish: "पूरा करें",
      doneTitle: "सब तैयार है!",
      doneSub: "आपका डैशबोर्ड तैयार है।",
      goToDashboard: "डैशबोर्ड पर जाएं",
      welcomeBackToast: "वापसी पर स्वागत है, {name}!",
      welcomeNewToast: "ToLetBook में आपका स्वागत है, {name}!",
      orDivider: "या",
      googleNoAccountToast: "उस Google ईमेल के लिए कोई ToLetBook अकाउंट नहीं मिला — चलिए आपको सेट अप करते हैं।",
      googleNoAccountNotice: "{email} के लिए कोई ToLetBook अकाउंट नहीं मिला। नीचे अकाउंट बनाएं: बताएं आप कौन हैं और अपना मोबाइल नंबर डालें।",
      forgotPasswordLink: "पासवर्ड भूल गए?",
      forgotPasswordEyebrow: "पासवर्ड रीसेट करें",
      forgotPasswordTitle: "अपना पासवर्ड भूल गए?",
      forgotPasswordSub: "अपना ईमेल डालें और हम आपको इसे रीसेट करने के लिए 6 अंकों का कोड भेजेंगे।",
      forgotPasswordSubmit: "कोड भेजें",
      forgotPasswordBusy: "भेजा जा रहा है…",
      forgotPasswordSentTitle: "अपना ईमेल जांचें",
      forgotPasswordSentSub: "अगर उस ईमेल के लिए कोई अकाउंट मौजूद है, तो हमने आपका पासवर्ड रीसेट करने के लिए 6 अंकों का कोड भेज दिया है।",
      forgotPasswordCodeLabel: "6 अंकों का कोड",
      forgotPasswordCodePlaceholder: "123456",
      forgotPasswordResendLink: "कोड नहीं मिला? फिर से भेजें",
      forgotPasswordResendToast: "कोड फिर से भेजा गया",
      forgotPasswordChangeEmailLink: "गलत ईमेल? वापस जाएं",
      resetPasswordEyebrow: "पासवर्ड रीसेट करें",
      resetPasswordTitle: "नया पासवर्ड चुनें",
      resetPasswordSub: "यह कोड 10 मिनट के लिए मान्य है।",
      newPasswordLabel: "नया पासवर्ड",
      confirmPasswordLabel: "नए पासवर्ड की पुष्टि करें",
      errPasswordMismatch: "पासवर्ड मेल नहीं खाते",
      errCodeLength: "6 अंकों का कोड डालें",
      resetPasswordSubmit: "पासवर्ड रीसेट करें",
      resetPasswordBusy: "रीसेट हो रहा है…",
      resetPasswordDoneTitle: "पासवर्ड रीसेट हो गया",
      resetPasswordDoneSub: "अब आप अपने नए पासवर्ड से लॉग इन कर सकते हैं।",
    },
    settings: {
      title: "सेटिंग्स",
      profileMenu: "प्रोफ़ाइल",
      reportsMenu: "रिपोर्ट्स",
      plansMenu: "प्लान और बिलिंग",
      referMenu: "रेफर करें और कमाएं",
      appearance: "थीम",
      language: "भाषा",
      dark: "डार्क",
      light: "लाइट",
      termsTitle: "नियम व शर्तें",
      privacyTitle: "गोपनीयता नीति",
      bankDetailsMenu: "मेरी बैंक डिटेल्स",
      bankDetailsHint: "इन डिटेल्स का इस्तेमाल आपको सीधे किराया और डिपॉज़िट भेजने के लिए किया जाता है। ये किराएदारों को कभी नहीं दिखाई जातीं।",
      bankDetailsSaved: "बैंक डिटेल्स सेव हो गईं",
      contactUsMenu: "संपर्क करें",
      emailUsedToLogIn: "ईमेल (लॉग इन के लिए इस्तेमाल होता है)",
      name: "नाम",
      phone: "फोन नंबर",
      phonePlaceholder: "10 अंकों का मोबाइल नंबर",
      city: "शहर",
      profileUpdated: "प्रोफ़ाइल अपडेट हो गई",
      demoNotice: "यहां के अकाउंट सिर्फ डेमो के लिए हैं; कोई भी ईमेल और पासवर्ड इस्तेमाल करें।",
      verificationTitle: "पहचान सत्यापन",
      verificationHint: "अपने प्रोफ़ाइल और लिस्टिंग पर वेरिफाइड बैज पाने के लिए सरकारी पहचान पत्र (आगे और पीछे) अपलोड करें।",
      verificationSubmit: "सत्यापन के लिए भेजें",
      verificationSubmitted: "सत्यापन के लिए भेज दिया गया",
      errIdPhotosRequired: "अपने पहचान पत्र के आगे और पीछे दोनों जोड़ें",
      verificationVerified: "आप सत्यापित हैं",
      verificationPending: "सत्यापन लंबित है",
      verificationPendingSub: "समीक्षा होते ही हम आपको सूचित करेंगे — आमतौर पर एक-दो दिन में।",
      verificationRejected: "सत्यापन स्वीकृत नहीं हुआ",
      changePasswordTitle: "पासवर्ड बदलें",
      currentPasswordLabel: "मौजूदा पासवर्ड",
      errCurrentPasswordRequired: "अपना मौजूदा पासवर्ड डालें",
      passwordChanged: "पासवर्ड बदल दिया गया",
      changePasswordButton: "पासवर्ड अपडेट करें",
    },
    notifications: {
      title: "नोटिफिकेशन",
      enabledToast: "नोटिफिकेशन ऑन कर दिए गए",
      disabledToast: "नोटिफिकेशन ऑफ कर दिए गए",
      couldNotEnableToast: "नोटिफिकेशन ऑन नहीं हो पाए",
      notConfigured: "इस सर्वर पर नोटिफिकेशन अभी सेट नहीं हैं",
      deniedHint: "आपकी डिवाइस सेटिंग्स में इस ऐप के लिए नोटिफिकेशन ब्लॉक हैं।",
      markAllRead: "सभी को पढ़ा हुआ चिह्नित करें",
      emptyState: "अभी तक कोई नोटिफिकेशन नहीं है।",
    },
    refer: {
      title: "रेफर करें और कमाएं",
      heroTitle: "1 महीना दें, 1 महीना पाएं",
      heroSub: "किसी दोस्त को ToLetBook पर आमंत्रित करें। जब वे ऐप डाउनलोड करके शुरुआत करें — ओनर के रूप में अपनी पहली प्रॉपर्टी लिस्ट करें, या टेनेंट के रूप में अपना पहला कॉन्टैक्ट अनलॉक करें — तो आप दोनों को 1 महीने का Pro मुफ़्त मिलेगा।",
      yourCodeLabel: "आपका रेफरल कोड",
      shareButton: "इनवाइट शेयर करें",
      copiedToast: "रेफरल लिंक कॉपी हो गया",
      statsReferred: "{count} आमंत्रित",
      statsRewarded: "{count} को इनाम मिला",
      statsRemaining: "{count} मुफ़्त महीने बाकी",
      howItWorksTitle: "यह कैसे काम करता है",
      step1: "अपना कोड या लिंक किसी दोस्त के साथ शेयर करें",
      step2: "वे ToLetBook डाउनलोड करें और साइन अप करें",
      step3: "वे ओनर के रूप में पहली प्रॉपर्टी लिस्ट करें, या टेनेंट के रूप में पहला कॉन्टैक्ट अनलॉक करें — आप दोनों को 1 महीना Pro मुफ़्त मिलेगा",
      historyTitle: "आपके रेफरल",
      emptyState: "अभी तक कोई रेफरल नहीं — शुरू करने के लिए अपना कोड शेयर करें",
      statusPending: "उनकी पहली लिस्टिंग का इंतज़ार है",
      statusRewarded: "इनाम मिला — 1 महीना मुफ़्त",
      statusCapped: "उन्हें इनाम मिला — आपकी सीमा पूरी हो गई",
      shareMessage: "मेरे साथ ToLetBook जॉइन करें! साइन अप करते समय मेरा रेफरल कोड {code} डालें — हम दोनों को 1 महीने का Pro मुफ़्त मिलेगा।",
    },
    ownerTerms: {
      intro: "ToLetBook इस्तेमाल करके आप इन शर्तों से सहमत होते हैं।",
      bullet1: "ToLetBook के ज़रिए किए गए किराए और डिपॉज़िट के भुगतान Razorpay के ज़रिए प्रोसेस होते हैं। ToLetBook कोई बैंक नहीं है और Razorpay द्वारा पुष्टि से आगे किसी नतीजे की गारंटी नहीं देता।",
      bullet2: "आपके द्वारा डाली गई प्रॉपर्टी, किराएदार और भुगतान की जानकारी सही होने की ज़िम्मेदारी आपकी है।",
      bullet3: "आपका डेटा सेवा चलाने के लिए सेव किया जाता है, किसी तीसरे पक्ष को नहीं बेचा जाता।",
      bullet4: "रिपोर्ट्स और नोटबुक के आंकड़े सीधे आपके डाले गए डेटा से निकाले जाते हैं — स्वतंत्र रूप से ऑडिट या सत्यापित नहीं हैं।",
      bullet5: "मैप और लोकेशन सुविधाएं Google Maps द्वारा दी जाती हैं, जो Google की अपनी शर्तों के अधीन हैं।",
    },
    tenantTerms: {
      intro: "ToLetBook इस्तेमाल करके आप इन शर्तों से सहमत होते हैं।",
      bullet1: "ToLetBook के ज़रिए किए गए किराए और डिपॉज़िट के भुगतान Razorpay के ज़रिए प्रोसेस होते हैं। ToLetBook कोई बैंक नहीं है और Razorpay द्वारा पुष्टि से आगे किसी नतीजे की गारंटी नहीं देता।",
      bullet2: "आपके द्वारा दी गई जानकारी सही होने की ज़िम्मेदारी आपकी है।",
      bullet3: "आपका डेटा सेवा चलाने के लिए सेव किया जाता है, किसी तीसरे पक्ष को नहीं बेचा जाता।",
      bullet4: "दिखाए गए आंकड़े आपके या आपके मालिक द्वारा डाले गए डेटा से सीधे निकाले जाते हैं — स्वतंत्र रूप से ऑडिट या सत्यापित नहीं हैं।",
      bullet5: "मैप और लोकेशन सुविधाएं Google Maps द्वारा दी जाती हैं, जो Google की अपनी शर्तों के अधीन हैं।",
    },
  },
  te: {
    common: {
      save: "మార్పులను సేవ్ చేయండి",
      saving: "సేవ్ అవుతోంది…",
      cancel: "రద్దు చేయండి",
      remove: "తొలగించండి",
      continue: "కొనసాగించండి",
      somethingWrong: "ఏదో తప్పు జరిగింది",
      logOut: "లాగ్ అవుట్",
      welcomeBackGreet: "తిరిగి స్వాగతం,",
      hiGreet: "నమస్తే,",
      statusOpen: "ఓపెన్",
      statusInProgress: "పురోగతిలో",
      statusResolved: "పరిష్కారమైంది",
    },
    requests: {
      categoryLabel: "వర్గం (ఐచ్ఛికం)",
      categoryPlumbing: "ప్లంబింగ్",
      categoryElectrical: "ఎలక్ట్రికల్",
      categoryAppliance: "ఉపకరణం",
      categoryFurniture: "ఫర్నిచర్",
      categoryCleaning: "క్లీనింగ్",
      categoryPestControl: "పెస్ట్ కంట్రోల్",
      categoryOther: "ఇతర",
      urgentCheckboxLabel: "ఇది అత్యవసరం",
      urgentBadge: "అత్యవసరం",
      photoLabel: "ఫోటో జోడించండి (ఐచ్ఛికం)",
      addPhotoBtn: "ఫోటో జోడించండి",
      removePhotoAria: "ఫోటో తీసివేయండి",
      updatesHeading: "అప్‌డేట్‌లు",
      noUpdatesYet: "ఇంకా ఎలాంటి అప్‌డేట్ లేదు.",
      addCommentPlaceholder: "ఒక నోట్ జోడించండి…",
      postCommentBtn: "పోస్ట్ చేయండి",
      postingCommentBtn: "పోస్ట్ చేస్తోంది…",
      callBtn: "కాల్ చేయండి",
      whatsappBtn: "వాట్సాప్",
    },
    nav: {
      home: "హోమ్",
      listings: "లిస్టింగ్స్",
      notebook: "నోట్‌బుక్",
      requests: "రిక్వెస్ట్‌లు",
      settings: "సెట్టింగ్‌లు",
      browse: "ప్రాపర్టీలు చూడండి",
      payments: "పేమెంట్స్",
      ownerDashboard: "యజమాని డాష్‌బోర్డ్",
      tenantDashboard: "అద్దెదారు డాష్‌బోర్డ్",
    },
    auth: {
      welcomeEyebrow: "స్వాగతం",
      welcomeTitle: "ToLetBook కి స్వాగతం",
      welcomeSub: "మీ ఖాతాలోకి లాగిన్ అవ్వండి, లేదా కొత్తది సృష్టించండి.",
      logIn: "లాగిన్",
      createAccount: "ఖాతా సృష్టించండి",
      loginTitle: "తిరిగి స్వాగతం",
      loginSub: "మీ డాష్‌బోర్డ్‌కి వెళ్లడానికి లాగిన్ అవ్వండి.",
      registerTitle: "మీ ఖాతాను సృష్టించండి",
      registerSub: "ప్రాపర్టీని లిస్ట్ చేయండి లేదా మీ తదుపరి ఇంటిని కనుగొనండి — బ్రోకర్లు లేకుండా, ఇబ్బంది లేకుండా.",
      passwordHint: "కనీసం 6 అక్షరాలు.",
      referralCodeLabel: "రెఫరల్ కోడ్ (ఐచ్ఛికం)",
      referralCodePlaceholder: "మీ దగ్గర కోడ్ ఉంటే నమోదు చేయండి",
      agreeTermsText: "నేను ToLetBook యొక్క నిబంధనలు & షరతులు మరియు గోప్యతా విధానానికి అంగీకరిస్తున్నాను",
      viewTermsLink: "నిబంధనలు & షరతులు చూడండి",
      viewPrivacyLink: "గోప్యతా విధానం చూడండి",
      hideTermsLink: "నిబంధనలు & షరతులు దాచండి",
      errMustAgreeTerms: "కొనసాగించడానికి దయచేసి నిబంధనలు & షరతులకు అంగీకరించండి",
      loginBusy: "లాగిన్ అవుతోంది…",
      emailLabel: "ఇమెయిల్",
      emailPlaceholder: "you@example.com",
      passwordLabel: "పాస్‌వర్డ్",
      passwordPlaceholder: "మీ పాస్‌వర్డ్",
      errEmailPassword: "మీ ఇమెయిల్ మరియు పాస్‌వర్డ్ నమోదు చేయండి",
      getStartedEyebrow: "ప్రారంభించండి",
      roleTitle: "మీరు ఎవరో చెప్పండి",
      roleSub: "తద్వారా మేము మీకు సరైన డాష్‌బోర్డ్ సెటప్ చేయగలం.",
      roleOwnerTitle: "నేను ప్రాపర్టీ / పీజీ యజమానిని",
      roleOwnerSub: "ప్రాపర్టీలు లిస్ట్ చేయండి, అద్దెదారులను నిర్వహించండి, అద్దె ట్రాక్ చేయండి",
      roleTenantTitle: "నేను అద్దెదారుని",
      roleTenantSub: "లిస్టింగ్‌లు చూడండి, అద్దె కట్టండి, రిక్వెస్ట్‌లు పంపండి",
      step1Eyebrow: "దశ 1",
      credentialsTitle: "మీ లాగిన్ సృష్టించండి",
      newPasswordPlaceholder: "కనీసం 6 అక్షరాలు",
      phoneOptionalLabel: "మొబైల్ నంబర్ (ఐచ్ఛికం)",
      phonePlaceholder: "10 అంకెల మొబైల్ నంబర్",
      errValidEmail: "సరైన ఇమెయిల్ చిరునామా నమోదు చేయండి",
      errPasswordLength: "పాస్‌వర్డ్ కనీసం 6 అక్షరాలు ఉండాలి",
      errValidPhoneOptional: "సరైన 10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి, లేదా ఖాళీగా వదిలేయండి",
      step2Eyebrow: "దశ 2",
      profileTitle: "మీ గురించి కొంచెం చెప్పండి",
      fullNameLabel: "పూర్తి పేరు",
      fullNamePlaceholder: "మీ పూర్తి పేరు",
      profileBusy: "దయచేసి వేచి ఉండండి…",
      errName: "మీ పేరు నమోదు చేయండి",
      step3Eyebrow: "దశ 3",
      propertyTypeTitle: "మీరు ఏమి నిర్వహిస్తారు?",
      propertyTypeSub: "దీని ఆధారంగా మీకు ఏ డాష్‌బోర్డ్ కనిపిస్తుందో నిర్ణయమవుతుంది — దీన్ని తర్వాత మార్చుకోవచ్చు.",
      typeIndependentTitle: "ఇండిపెండెంట్ ప్రాపర్టీలు",
      typeIndependentSub: "పూర్తి ఇళ్లు, ఫ్లాట్‌లు, విల్లాలు",
      typePgTitle: "పీజీ / హాస్టల్",
      typePgSub: "బెడ్ ప్రకారం అద్దె ఉండే షేర్డ్ గదులు",
      typeBothTitle: "రెండూ",
      typeBothSub: "ఇండిపెండెంట్ యూనిట్లు మరియు పీజీ గదుల మిశ్రమం",
      almostDoneEyebrow: "దాదాపు పూర్తయింది",
      cityTitle: "మీరు ఎక్కడ పనిచేస్తారు?",
      citySub: "దీని ఆధారంగా మీ లిస్టింగ్‌లను దగ్గరలోని అద్దెదారులకు ముందుగా చూపిస్తాము.",
      cityLabel: "నగరం",
      cityPlaceholder: "ఉదా. ముంబై",
      bankDetailsTitle: "మేము మీకు ఎలా చెల్లించాలి?",
      bankDetailsSub: "అద్దె మరియు డిపాజిట్ చెల్లింపులు నేరుగా మీకు పంపడానికి మీ బ్యాంక్ ఖాతాను జోడించండి.",
      payoutMethodUpi: "UPI ID",
      payoutMethodBank: "బ్యాంక్ ఖాతా",
      upiIdLabel: "UPI ID",
      upiIdPlaceholder: "ఉదా. name@okhdfcbank",
      errValidUpi: "సరైన UPI ID నమోదు చేయండి",
      bankAccountHolderLabel: "ఖాతాదారు పేరు",
      bankAccountHolderPlaceholder: "బ్యాంక్ రికార్డుల ప్రకారం",
      bankAccountNumberLabel: "ఖాతా నంబర్",
      bankAccountNumberPlaceholder: "బ్యాంక్ ఖాతా నంబర్",
      bankIfscLabel: "IFSC కోడ్",
      bankIfscPlaceholder: "ఉదా. HDFC0001234",
      bankDetailsHint: "మీరు దీన్ని ఎప్పుడైనా సెట్టింగ్స్ నుండి జోడించవచ్చు లేదా మార్చవచ్చు.",
      skipForNowLink: "ఇప్పుడు వదిలేయండి",
      errValidIfsc: "సరైన IFSC కోడ్ నమోదు చేయండి",
      finishBusy: "సెటప్ చేస్తోంది…",
      finish: "పూర్తి చేయండి",
      doneTitle: "మీరు సిద్ధంగా ఉన్నారు!",
      doneSub: "మీ డాష్‌బోర్డ్ సిద్ధంగా ఉంది.",
      goToDashboard: "డాష్‌బోర్డ్‌కు వెళ్లండి",
      welcomeBackToast: "తిరిగి స్వాగతం, {name}!",
      welcomeNewToast: "ToLetBook కి స్వాగతం, {name}!",
      orDivider: "లేదా",
      googleNoAccountToast: "ఆ Google ఇమెయిల్ కోసం ToLetBook ఖాతా కనుగొనబడలేదు — మిమ్మల్ని సెటప్ చేద్దాం.",
      googleNoAccountNotice: "{email} కోసం ToLetBook ఖాతా లేదు. కింద ఖాతా సృష్టించండి: మీరు ఎవరో ఎంచుకుని, మొబైల్ నంబర్ ఇవ్వండి.",
      forgotPasswordLink: "పాస్‌వర్డ్ మర్చిపోయారా?",
      forgotPasswordEyebrow: "పాస్‌వర్డ్ రీసెట్ చేయండి",
      forgotPasswordTitle: "మీ పాస్‌వర్డ్ మర్చిపోయారా?",
      forgotPasswordSub: "మీ ఇమెయిల్ నమోదు చేయండి, దాన్ని రీసెట్ చేయడానికి మేము మీకు 6 అంకెల కోడ్ పంపుతాము.",
      forgotPasswordSubmit: "కోడ్ పంపండి",
      forgotPasswordBusy: "పంపుతోంది…",
      forgotPasswordSentTitle: "మీ ఇమెయిల్ చూడండి",
      forgotPasswordSentSub: "ఆ ఇమెయిల్ కోసం ఖాతా ఉంటే, మీ పాస్‌వర్డ్‌ను రీసెట్ చేయడానికి మేము 6 అంకెల కోడ్ పంపాము.",
      forgotPasswordCodeLabel: "6 అంకెల కోడ్",
      forgotPasswordCodePlaceholder: "123456",
      forgotPasswordResendLink: "రాలేదా? కోడ్ మళ్ళీ పంపండి",
      forgotPasswordResendToast: "కోడ్ మళ్ళీ పంపబడింది",
      forgotPasswordChangeEmailLink: "తప్పు ఇమెయిలా? వెనక్కి వెళ్ళండి",
      resetPasswordEyebrow: "పాస్‌వర్డ్ రీసెట్ చేయండి",
      resetPasswordTitle: "కొత్త పాస్‌వర్డ్ ఎంచుకోండి",
      resetPasswordSub: "ఈ కోడ్ 10 నిమిషాల వరకు చెల్లుతుంది.",
      newPasswordLabel: "కొత్త పాస్‌వర్డ్",
      confirmPasswordLabel: "కొత్త పాస్‌వర్డ్‌ని నిర్ధారించండి",
      errCodeLength: "6 అంకెల కోడ్ నమోదు చేయండి",
      errPasswordMismatch: "పాస్‌వర్డ్‌లు సరిపోలలేదు",
      resetPasswordSubmit: "పాస్‌వర్డ్ రీసెట్ చేయండి",
      resetPasswordBusy: "రీసెట్ అవుతోంది…",
      resetPasswordDoneTitle: "పాస్‌వర్డ్ రీసెట్ అయ్యింది",
      resetPasswordDoneSub: "ఇప్పుడు మీరు మీ కొత్త పాస్‌వర్డ్‌తో లాగిన్ కావచ్చు.",
    },
    settings: {
      title: "సెట్టింగ్‌లు",
      profileMenu: "ప్రొఫైల్",
      reportsMenu: "రిపోర్ట్‌లు",
      plansMenu: "ప్లాన్‌లు & బిల్లింగ్",
      referMenu: "రెఫర్ చేసి సంపాదించండి",
      appearance: "థీమ్",
      language: "భాష",
      dark: "డార్క్",
      light: "లైట్",
      termsTitle: "నిబంధనలు & షరతులు",
      privacyTitle: "గోప్యతా విధానం",
      bankDetailsMenu: "నా బ్యాంక్ వివరాలు",
      bankDetailsHint: "మీకు అద్దె మరియు డిపాజిట్ చెల్లింపులు నేరుగా పంపడానికి ఈ వివరాలు ఉపయోగించబడతాయి. ఇవి అద్దెదారులకు ఎప్పుడూ చూపించబడవు.",
      bankDetailsSaved: "బ్యాంక్ వివరాలు సేవ్ అయ్యాయి",
      contactUsMenu: "మమ్మల్ని సంప్రదించండి",
      emailUsedToLogIn: "ఇమెయిల్ (లాగిన్ కోసం ఉపయోగిస్తారు)",
      name: "పేరు",
      phone: "ఫోన్ నంబర్",
      phonePlaceholder: "10 అంకెల మొబైల్ నంబర్",
      city: "నగరం",
      profileUpdated: "ప్రొఫైల్ అప్‌డేట్ అయ్యింది",
      demoNotice: "ఇక్కడి ఖాతాలు కేవలం డెమో కోసమే; ఏదైనా ఇమెయిల్ మరియు పాస్‌వర్డ్ వాడండి.",
      verificationTitle: "గుర్తింపు ధృవీకరణ",
      verificationHint: "మీ ప్రొఫైల్ మరియు లిస్టింగ్‌లపై వెరిఫైడ్ బ్యాడ్జ్ పొందడానికి ప్రభుత్వ గుర్తింపు కార్డు (ముందు మరియు వెనుక) అప్‌లోడ్ చేయండి.",
      verificationSubmit: "ధృవీకరణ కోసం సమర్పించండి",
      verificationSubmitted: "ధృవీకరణ కోసం సమర్పించబడింది",
      errIdPhotosRequired: "మీ గుర్తింపు కార్డు ముందు మరియు వెనుక రెండూ జోడించండి",
      verificationVerified: "మీరు ధృవీకరించబడ్డారు",
      verificationPending: "ధృవీకరణ పెండింగ్‌లో ఉంది",
      verificationPendingSub: "సమీక్షించిన వెంటనే మేము మీకు తెలియజేస్తాము — సాధారణంగా ఒకటి రెండు రోజుల్లో.",
      verificationRejected: "ధృవీకరణ ఆమోదించబడలేదు",
      changePasswordTitle: "పాస్‌వర్డ్ మార్చండి",
      currentPasswordLabel: "ప్రస్తుత పాస్‌వర్డ్",
      errCurrentPasswordRequired: "మీ ప్రస్తుత పాస్‌వర్డ్ నమోదు చేయండి",
      passwordChanged: "పాస్‌వర్డ్ మార్చబడింది",
      changePasswordButton: "పాస్‌వర్డ్ అప్‌డేట్ చేయండి",
    },
    notifications: {
      title: "నోటిఫికేషన్‌లు",
      enabledToast: "నోటిఫికేషన్‌లు ఆన్ చేయబడ్డాయి",
      disabledToast: "నోటిఫికేషన్‌లు ఆఫ్ చేయబడ్డాయి",
      couldNotEnableToast: "నోటిఫికేషన్‌లు ఆన్ చేయలేకపోయాము",
      notConfigured: "ఈ సర్వర్‌లో నోటిఫికేషన్‌లు ఇంకా సెటప్ చేయలేదు",
      deniedHint: "మీ డివైస్ సెట్టింగ్‌లలో ఈ యాప్ కోసం నోటిఫికేషన్‌లు బ్లాక్ చేయబడ్డాయి.",
      markAllRead: "అన్నీ చదివినట్లు గుర్తించండి",
      emptyState: "ఇంకా నోటిఫికేషన్‌లు లేవు.",
    },
    refer: {
      title: "రెఫర్ చేసి సంపాదించండి",
      heroTitle: "1 నెల ఇవ్వండి, 1 నెల పొందండి",
      heroSub: "మీ స్నేహితుడిని ToLetBook కి ఆహ్వానించండి. వారు యాప్ డౌన్‌లోడ్ చేసి ప్రారంభించిన వెంటనే — ఓనర్‌గా తమ మొదటి ప్రాపర్టీని లిస్ట్ చేసినా, లేదా టెనెంట్‌గా తమ మొదటి కాంటాక్ట్‌ను అన్‌లాక్ చేసినా — మీ ఇద్దరికీ 1 నెల Pro ఉచితంగా లభిస్తుంది.",
      yourCodeLabel: "మీ రెఫరల్ కోడ్",
      shareButton: "ఆహ్వానం షేర్ చేయండి",
      copiedToast: "రెఫరల్ లింక్ కాపీ అయింది",
      statsReferred: "{count} మంది ఆహ్వానించారు",
      statsRewarded: "{count} మందికి రివార్డ్ వచ్చింది",
      statsRemaining: "{count} ఉచిత నెలలు మిగిలి ఉన్నాయి",
      howItWorksTitle: "ఇది ఎలా పనిచేస్తుంది",
      step1: "మీ కోడ్ లేదా లింక్‌ను స్నేహితుడితో షేర్ చేయండి",
      step2: "వారు ToLetBook డౌన్‌లోడ్ చేసి సైన్ అప్ చేస్తారు",
      step3: "వారు ఓనర్‌గా మొదటి ప్రాపర్టీ లిస్ట్ చేస్తారు, లేదా టెనెంట్‌గా మొదటి కాంటాక్ట్ అన్‌లాక్ చేస్తారు — మీ ఇద్దరికీ 1 నెల Pro ఉచితంగా లభిస్తుంది",
      historyTitle: "మీ రెఫరల్స్",
      emptyState: "ఇంకా రెఫరల్స్ లేవు — ప్రారంభించడానికి మీ కోడ్ షేర్ చేయండి",
      statusPending: "వారి మొదటి లిస్టింగ్ కోసం వేచి ఉంది",
      statusRewarded: "రివార్డ్ వచ్చింది — 1 నెల ఉచితం",
      statusCapped: "వారికి రివార్డ్ వచ్చింది — మీ పరిమితి పూర్తయింది",
      shareMessage: "నాతో కలిసి ToLetBook లో చేరండి! సైన్ అప్ చేసేటప్పుడు నా రెఫరల్ కోడ్ {code} వాడండి — మనం ఇద్దరం 1 నెల Pro ఉచితంగా పొందుతాము.",
    },
    ownerTerms: {
      intro: "ToLetBook ఉపయోగించడం ద్వారా మీరు ఈ నిబంధనలకు అంగీకరిస్తున్నారు.",
      bullet1: "ToLetBook ద్వారా చేసే అద్దె మరియు డిపాజిట్ చెల్లింపులు Razorpay ద్వారా ప్రాసెస్ చేయబడతాయి. ToLetBook బ్యాంక్ కాదు మరియు Razorpay నిర్ధారించిన దానికి మించి ఏ ఫలితానికి హామీ ఇవ్వదు.",
      bullet2: "మీరు నమోదు చేసే ప్రాపర్టీ, అద్దెదారు మరియు చెల్లింపు వివరాలు సరిగ్గా ఉండేలా చూసుకోవడం మీ బాధ్యత.",
      bullet3: "మీరు నమోదు చేసే ప్రాపర్టీ, అద్దెదారు మరియు చెల్లింపు వివరాలు ఈ డెమో కోసం మాత్రమే నిల్వ చేయబడతాయి, మూడో వ్యక్తులతో పంచుకోబడవు.",
      bullet4: "రిపోర్ట్‌లు మరియు నోట్‌బుక్‌లోని గణాంకాలు మీరు నమోదు చేసిన డేటా నుండి నేరుగా లెక్కించబడతాయి — ఆడిట్ చేయబడలేదు.",
      bullet5: "మ్యాప్ మరియు లొకేషన్ ఫీచర్లు Google Maps ద్వారా అందించబడతాయి, ఇవి Google నిబంధనలకు లోబడి ఉంటాయి.",
    },
    tenantTerms: {
      intro: "ToLetBook ఉపయోగించడం ద్వారా మీరు ఈ నిబంధనలకు అంగీకరిస్తున్నారు.",
      bullet1: "ToLetBook ద్వారా చేసే అద్దె మరియు డిపాజిట్ చెల్లింపులు Razorpay ద్వారా ప్రాసెస్ చేయబడతాయి. ToLetBook బ్యాంక్ కాదు మరియు Razorpay నిర్ధారించిన దానికి మించి ఏ ఫలితానికి హామీ ఇవ్వదు.",
      bullet2: "మీరు అందించే సమాచారం సరిగ్గా ఉండేలా చూసుకోవడం మీ బాధ్యత.",
      bullet3: "మీరు నమోదు చేసే ప్రాపర్టీ, అద్దెదారు మరియు చెల్లింపు వివరాలు ఈ డెమో కోసం మాత్రమే నిల్వ చేయబడతాయి, మూడో వ్యక్తులతో పంచుకోబడవు.",
      bullet4: "చూపిన గణాంకాలు మీరు లేదా మీ యజమాని నమోదు చేసిన డేటా నుండి నేరుగా లెక్కించబడతాయి — ఆడిట్ చేయబడలేదు.",
      bullet5: "మ్యాప్ మరియు లొకేషన్ ఫీచర్లు Google Maps ద్వారా అందించబడతాయి, ఇవి Google నిబంధనలకు లోబడి ఉంటాయి.",
    },
  },
  ta: {
    common: {
      save: "மாற்றங்களைச் சேமி",
      saving: "சேமிக்கிறது…",
      cancel: "ரத்து செய்",
      remove: "அகற்று",
      continue: "தொடரவும்",
      somethingWrong: "ஏதோ தவறு நடந்தது",
      logOut: "வெளியேறு",
      welcomeBackGreet: "மீண்டும் வரவேற்கிறோம்,",
      hiGreet: "வணக்கம்,",
      statusOpen: "திறந்துள்ளது",
      statusInProgress: "செயலில் உள்ளது",
      statusResolved: "தீர்க்கப்பட்டது",
    },
    requests: {
      categoryLabel: "வகை (விருப்பம்)",
      categoryPlumbing: "பிளம்பிங்",
      categoryElectrical: "மின்சாரம்",
      categoryAppliance: "உபகரணம்",
      categoryFurniture: "மரச்சாமான்கள்",
      categoryCleaning: "சுத்தம் செய்தல்",
      categoryPestControl: "பூச்சி கட்டுப்பாடு",
      categoryOther: "மற்றவை",
      urgentCheckboxLabel: "இது அவசரமானது",
      urgentBadge: "அவசரம்",
      photoLabel: "புகைப்படம் சேர்க்கவும் (விருப்பம்)",
      addPhotoBtn: "புகைப்படம் சேர்",
      removePhotoAria: "புகைப்படத்தை அகற்று",
      updatesHeading: "புதுப்பிப்புகள்",
      noUpdatesYet: "இதுவரை புதுப்பிப்புகள் இல்லை.",
      addCommentPlaceholder: "ஒரு குறிப்பைச் சேர்க்கவும்…",
      postCommentBtn: "பதிவிடு",
      postingCommentBtn: "பதிவிடுகிறது…",
      callBtn: "அழைக்க",
      whatsappBtn: "வாட்ஸ்அப்",
    },
    nav: {
      home: "முகப்பு",
      listings: "பட்டியல்கள்",
      notebook: "நோட்புக்",
      requests: "கோரிக்கைகள்",
      settings: "அமைப்புகள்",
      browse: "சொத்துகளைப் பார்",
      payments: "பணம் செலுத்துதல்",
      ownerDashboard: "உரிமையாளர் டாஷ்போர்டு",
      tenantDashboard: "குடியிருப்பாளர் டாஷ்போர்டு",
    },
    auth: {
      welcomeEyebrow: "வரவேற்பு",
      welcomeTitle: "ToLetBook-க்கு வரவேற்கிறோம்",
      welcomeSub: "உங்கள் கணக்கில் உள்நுழையவும், அல்லது புதிதாக உருவாக்கவும்.",
      logIn: "உள்நுழை",
      createAccount: "கணக்கை உருவாக்கு",
      loginTitle: "மீண்டும் வரவேற்கிறோம்",
      loginSub: "உங்கள் டாஷ்போர்டுக்குச் செல்ல உள்நுழையவும்.",
      registerTitle: "உங்கள் கணக்கை உருவாக்கவும்",
      registerSub: "ஒரு சொத்தை பட்டியலிடுங்கள் அல்லது உங்கள் அடுத்த வீட்டைக் கண்டறியுங்கள் — தரகர்கள் இல்லாமல், சிரமமில்லாமல்.",
      passwordHint: "குறைந்தது 6 எழுத்துகள்.",
      referralCodeLabel: "பரிந்துரை குறியீடு (விருப்பமானது)",
      referralCodePlaceholder: "உங்களிடம் குறியீடு இருந்தால் உள்ளிடவும்",
      agreeTermsText: "நான் ToLetBook இன் விதிமுறைகள் & நிபந்தனைகள் மற்றும் தனியுரிமைக் கொள்கையை ஒப்புக்கொள்கிறேன்",
      viewTermsLink: "விதிமுறைகள் & நிபந்தனைகளைக் காண்க",
      viewPrivacyLink: "தனியுரிமைக் கொள்கையைக் காண்க",
      hideTermsLink: "விதிமுறைகள் & நிபந்தனைகளை மறை",
      errMustAgreeTerms: "தொடர விதிமுறைகள் & நிபந்தனைகளை ஏற்கவும்",
      loginBusy: "உள்நுழைகிறது…",
      emailLabel: "மின்னஞ்சல்",
      emailPlaceholder: "you@example.com",
      passwordLabel: "கடவுச்சொல்",
      passwordPlaceholder: "உங்கள் கடவுச்சொல்",
      errEmailPassword: "உங்கள் மின்னஞ்சல் மற்றும் கடவுச்சொல்லை உள்ளிடவும்",
      getStartedEyebrow: "தொடங்குவோம்",
      roleTitle: "நீங்கள் யார் என்று சொல்லுங்கள்",
      roleSub: "அதனால் உங்களுக்கு சரியான டாஷ்போர்டை அமைக்க முடியும்.",
      roleOwnerTitle: "நான் ஒரு சொத்து / பிஜி உரிமையாளர்",
      roleOwnerSub: "சொத்துகளை பட்டியலிடுங்கள், குடியிருப்பாளர்களை நிர்வகிக்கவும், வாடகையை கண்காணிக்கவும்",
      roleTenantTitle: "நான் ஒரு குடியிருப்பாளர்",
      roleTenantSub: "பட்டியல்களை பார்க்கவும், வாடகை செலுத்தவும், கோரிக்கைகள் அனுப்பவும்",
      step1Eyebrow: "படி 1",
      credentialsTitle: "உங்கள் உள்நுழைவை உருவாக்குங்கள்",
      newPasswordPlaceholder: "குறைந்தது 6 எழுத்துகள்",
      phoneOptionalLabel: "மொபைல் எண் (விருப்பத்திற்குரியது)",
      phonePlaceholder: "10 இலக்க மொபைல் எண்",
      errValidEmail: "சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்",
      errPasswordLength: "கடவுச்சொல் குறைந்தது 6 எழுத்துகள் இருக்க வேண்டும்",
      errValidPhoneOptional: "சரியான 10 இலக்க மொபைல் எண்ணை உள்ளிடவும், அல்லது காலியாக விடவும்",
      step2Eyebrow: "படி 2",
      profileTitle: "உங்களைப் பற்றி கொஞ்சம்",
      fullNameLabel: "முழு பெயர்",
      fullNamePlaceholder: "உங்கள் முழு பெயர்",
      profileBusy: "தயவுசெய்து காத்திருக்கவும்…",
      errName: "உங்கள் பெயரை உள்ளிடவும்",
      step3Eyebrow: "படி 3",
      propertyTypeTitle: "நீங்கள் எதை நிர்வகிக்கிறீர்கள்?",
      propertyTypeSub: "இது உங்களுக்கு எந்த டாஷ்போர்டு காட்சிகள் தெரியும் என்பதை தீர்மானிக்கும் — இதை பின்னர் மாற்றலாம்.",
      typeIndependentTitle: "தனி சொத்துகள்",
      typeIndependentSub: "முழு வீடுகள், ஃப்ளாட்கள், வில்லாக்கள்",
      typePgTitle: "பிஜி / விடுதி",
      typePgSub: "படுக்கை வாரியாக வாடகை வசூலிக்கப்படும் பகிர்வு அறைகள்",
      typeBothTitle: "இரண்டும்",
      typeBothSub: "தனி யூனிட்கள் மற்றும் பிஜி அறைகளின் கலவை",
      almostDoneEyebrow: "கிட்டத்தட்ட முடிந்தது",
      cityTitle: "நீங்கள் எங்கு செயல்படுகிறீர்கள்?",
      citySub: "இதன் மூலம் உங்கள் பட்டியல்களை அருகிலுள்ள வாடகைதாரர்களுக்கு முதலில் காட்டுவோம்.",
      cityLabel: "நகரம்",
      cityPlaceholder: "எ.கா. மும்பை",
      bankDetailsTitle: "உங்களுக்கு எப்படி பணம் செலுத்த வேண்டும்?",
      bankDetailsSub: "வாடகை மற்றும் டெபாசிட் பணம் நேரடியாக உங்களுக்கு அனுப்ப உங்கள் வங்கிக் கணக்கைச் சேர்க்கவும்.",
      payoutMethodUpi: "UPI ஐடி",
      payoutMethodBank: "வங்கி கணக்கு",
      upiIdLabel: "UPI ஐடி",
      upiIdPlaceholder: "எ.கா. name@okhdfcbank",
      errValidUpi: "சரியான UPI ஐடியை உள்ளிடவும்",
      bankAccountHolderLabel: "கணக்குதாரர் பெயர்",
      bankAccountHolderPlaceholder: "வங்கி பதிவுகளின் படி",
      bankAccountNumberLabel: "கணக்கு எண்",
      bankAccountNumberPlaceholder: "வங்கி கணக்கு எண்",
      bankIfscLabel: "IFSC குறியீடு",
      bankIfscPlaceholder: "எ.கா. HDFC0001234",
      bankDetailsHint: "இதை நீங்கள் எப்போது வேண்டுமானாலும் அமைப்புகளில் இருந்து சேர்க்கலாம் அல்லது மாற்றலாம்.",
      skipForNowLink: "இப்போது தவிர்க்க",
      errValidIfsc: "சரியான IFSC குறியீட்டை உள்ளிடவும்",
      finishBusy: "அமைக்கிறது…",
      finish: "முடி",
      doneTitle: "நீங்கள் தயார்!",
      doneSub: "உங்கள் டாஷ்போர்டு தயார்.",
      goToDashboard: "டாஷ்போர்டுக்குச் செல்",
      welcomeBackToast: "மீண்டும் வரவேற்கிறோம், {name}!",
      welcomeNewToast: "ToLetBook-க்கு வரவேற்கிறோம், {name}!",
      orDivider: "அல்லது",
      googleNoAccountToast: "அந்த Google மின்னஞ்சலுக்கு ToLetBook கணக்கு எதுவும் கிடைக்கவில்லை — உங்களை அமைப்போம்.",
      forgotPasswordLink: "கடவுச்சொல் மறந்துவிட்டதா?",
      forgotPasswordEyebrow: "கடவுச்சொல்லை மீட்டமை",
      forgotPasswordTitle: "உங்கள் கடவுச்சொல்லை மறந்துவிட்டீர்களா?",
      forgotPasswordSub: "உங்கள் மின்னஞ்சலை உள்ளிடவும், அதை மீட்டமைக்க 6-இலக்க குறியீட்டை அனுப்புவோம்.",
      forgotPasswordSubmit: "குறியீட்டை அனுப்பு",
      forgotPasswordBusy: "அனுப்புகிறது…",
      forgotPasswordSentTitle: "உங்கள் மின்னஞ்சலைப் பாருங்கள்",
      forgotPasswordSentSub: "அந்த மின்னஞ்சலுக்கு கணக்கு இருந்தால், உங்கள் கடவுச்சொல்லை மீட்டமைக்க 6-இலக்க குறியீட்டை அனுப்பியுள்ளோம்.",
      forgotPasswordCodeLabel: "6-இலக்க குறியீடு",
      forgotPasswordCodePlaceholder: "123456",
      forgotPasswordResendLink: "வரவில்லையா? குறியீட்டை மீண்டும் அனுப்பு",
      forgotPasswordResendToast: "குறியீடு மீண்டும் அனுப்பப்பட்டது",
      forgotPasswordChangeEmailLink: "தவறான மின்னஞ்சலா? திரும்பிச் செல்",
      resetPasswordEyebrow: "கடவுச்சொல்லை மீட்டமை",
      resetPasswordTitle: "புதிய கடவுச்சொல்லைத் தேர்ந்தெடுக்கவும்",
      resetPasswordSub: "இந்த குறியீடு 10 நிமிடங்கள் செல்லுபடியாகும்.",
      newPasswordLabel: "புதிய கடவுச்சொல்",
      confirmPasswordLabel: "புதிய கடவுச்சொல்லை உறுதிப்படுத்தவும்",
      errPasswordMismatch: "கடவுச்சொற்கள் பொருந்தவில்லை",
      errCodeLength: "6-இலக்க குறியீட்டை உள்ளிடவும்",
      resetPasswordSubmit: "கடவுச்சொல்லை மீட்டமை",
      resetPasswordBusy: "மீட்டமைக்கிறது…",
      resetPasswordDoneTitle: "கடவுச்சொல் மீட்டமைக்கப்பட்டது",
      resetPasswordDoneSub: "இப்போது உங்கள் புதிய கடவுச்சொல்லுடன் உள்நுழையலாம்.",
    },
    settings: {
      title: "அமைப்புகள்",
      profileMenu: "சுயவிவரம்",
      reportsMenu: "அறிக்கைகள்",
      plansMenu: "திட்டங்கள் & பில்லிங்",
      referMenu: "பரிந்துரைத்து சம்பாதிக்கவும்",
      appearance: "தீம்",
      language: "மொழி",
      dark: "இருள்",
      light: "வெளிச்சம்",
      termsTitle: "விதிமுறைகள் & நிபந்தனைகள்",
      privacyTitle: "தனியுரிமைக் கொள்கை",
      bankDetailsMenu: "எனது வங்கி விவரங்கள்",
      bankDetailsHint: "வாடகை மற்றும் டெபாசிட் பணத்தை நேரடியாக உங்களுக்கு அனுப்ப இந்த விவரங்கள் பயன்படுத்தப்படும். இவை வாடகைதாரர்களுக்குக் காட்டப்படாது.",
      bankDetailsSaved: "வங்கி விவரங்கள் சேமிக்கப்பட்டன",
      contactUsMenu: "எங்களை தொடர்பு கொள்ள",
      emailUsedToLogIn: "மின்னஞ்சல் (உள்நுழைய பயன்படுத்தப்படுகிறது)",
      name: "பெயர்",
      phone: "தொலைபேசி எண்",
      phonePlaceholder: "10 இலக்க மொபைல் எண்",
      city: "நகரம்",
      profileUpdated: "சுயவிவரம் புதுப்பிக்கப்பட்டது",
      demoNotice: "இங்குள்ள கணக்குகள் டெமோ நோக்கத்திற்காக மட்டுமே; ஏதேனும் மின்னஞ்சல் மற்றும் கடவுச்சொல்லை பயன்படுத்தவும்.",
      verificationTitle: "அடையாள சரிபார்ப்பு",
      verificationHint: "உங்கள் சுயவிவரம் மற்றும் பட்டியல்களில் வெரிஃபைட் பேட்ஜ் பெற அரசு அடையாள அட்டையை (முன் மற்றும் பின்) பதிவேற்றவும்.",
      verificationSubmit: "சரிபார்ப்புக்கு சமர்ப்பிக்கவும்",
      verificationSubmitted: "சரிபார்ப்புக்கு சமர்ப்பிக்கப்பட்டது",
      errIdPhotosRequired: "உங்கள் அடையாள அட்டையின் முன் மற்றும் பின் இரண்டையும் சேர்க்கவும்",
      verificationVerified: "நீங்கள் சரிபார்க்கப்பட்டுள்ளீர்கள்",
      verificationPending: "சரிபார்ப்பு நிலுவையில் உள்ளது",
      verificationPendingSub: "மதிப்பாய்வு முடிந்தவுடன் உங்களுக்கு தெரிவிப்போம் — பொதுவாக ஒன்று அல்லது இரண்டு நாட்களில்.",
      verificationRejected: "சரிபார்ப்பு ஏற்கப்படவில்லை",
      changePasswordTitle: "கடவுச்சொல்லை மாற்று",
      currentPasswordLabel: "தற்போதைய கடவுச்சொல்",
      errCurrentPasswordRequired: "உங்கள் தற்போதைய கடவுச்சொல்லை உள்ளிடவும்",
      passwordChanged: "கடவுச்சொல் மாற்றப்பட்டது",
      changePasswordButton: "கடவுச்சொல்லைப் புதுப்பி",
    },
    notifications: {
      title: "அறிவிப்புகள்",
      enabledToast: "அறிவிப்புகள் இயக்கப்பட்டன",
      disabledToast: "அறிவிப்புகள் முடக்கப்பட்டன",
      couldNotEnableToast: "அறிவிப்புகளை இயக்க முடியவில்லை",
      notConfigured: "இந்த சர்வரில் அறிவிப்புகள் இன்னும் அமைக்கப்படவில்லை",
      deniedHint: "உங்கள் சாதன அமைப்புகளில் இந்த ஆப்பிற்கான அறிவிப்புகள் தடுக்கப்பட்டுள்ளன.",
      markAllRead: "அனைத்தையும் படித்ததாகக் குறி",
      emptyState: "இதுவரை அறிவிப்புகள் இல்லை.",
    },
    refer: {
      title: "பரிந்துரைத்து சம்பாதிக்கவும்",
      heroTitle: "1 மாதம் கொடுங்கள், 1 மாதம் பெறுங்கள்",
      heroSub: "உங்கள் நண்பரை ToLetBook க்கு அழைக்கவும். அவர்கள் ஆப்பைப் பதிவிறக்கி தொடங்கியவுடன் — உரிமையாளராக தங்கள் முதல் சொத்தை பட்டியலிட்டாலும், அல்லது வாடகைதாரராக தங்கள் முதல் தொடர்பை அன்லாக் செய்தாலும் — நீங்கள் இருவரும் 1 மாத Pro இலவசமாகப் பெறுவீர்கள்.",
      yourCodeLabel: "உங்கள் பரிந்துரை குறியீடு",
      shareButton: "அழைப்பைப் பகிரவும்",
      copiedToast: "பரிந்துரை இணைப்பு நகலெடுக்கப்பட்டது",
      statsReferred: "{count} பேர் அழைக்கப்பட்டனர்",
      statsRewarded: "{count} பேருக்கு வெகுமதி கிடைத்தது",
      statsRemaining: "{count} இலவச மாதங்கள் மீதம்",
      howItWorksTitle: "இது எப்படி வேலை செய்கிறது",
      step1: "உங்கள் குறியீடு அல்லது இணைப்பை ஒரு நண்பருடன் பகிரவும்",
      step2: "அவர்கள் ToLetBook ஐப் பதிவிறக்கி பதிவு செய்வார்கள்",
      step3: "அவர்கள் உரிமையாளராக முதல் சொத்தை பட்டியலிடுவார்கள், அல்லது வாடகைதாரராக முதல் தொடர்பை அன்லாக் செய்வார்கள் — நீங்கள் இருவரும் 1 மாத Pro இலவசமாகப் பெறுவீர்கள்",
      historyTitle: "உங்கள் பரிந்துரைகள்",
      emptyState: "இதுவரை பரிந்துரைகள் இல்லை — தொடங்க உங்கள் குறியீட்டைப் பகிரவும்",
      statusPending: "அவர்களின் முதல் பட்டியலுக்காகக் காத்திருக்கிறது",
      statusRewarded: "வெகுமதி கிடைத்தது — 1 மாதம் இலவசம்",
      statusCapped: "அவர்களுக்கு வெகுமதி கிடைத்தது — உங்கள் வரம்பு முடிந்தது",
      shareMessage: "என்னுடன் ToLetBook இல் சேரவும்! பதிவு செய்யும்போது என் பரிந்துரை குறியீடு {code} ஐப் பயன்படுத்தவும் — நாம் இருவரும் 1 மாத Pro இலவசமாகப் பெறுவோம்.",
    },
    ownerTerms: {
      intro: "ToLetBook-ஐ பயன்படுத்துவதன் மூலம் நீங்கள் இந்த விதிமுறைகளுக்கு ஒப்புக்கொள்கிறீர்கள்.",
      bullet1: "ToLetBook மூலம் செய்யப்படும் வாடகை மற்றும் டெபாசிட் கட்டணங்கள் Razorpay மூலம் செயலாக்கப்படுகின்றன. ToLetBook ஒரு வங்கி அல்ல, மேலும் Razorpay உறுதிப்படுத்துவதற்கு அப்பால் எந்த முடிவுக்கும் உத்தரவாதம் அளிக்காது.",
      bullet2: "நீங்கள் உள்ளிடும் சொத்து, குடியிருப்பாளர் மற்றும் கட்டண தகவல்கள் சரியாக இருப்பது உங்கள் பொறுப்பு.",
      bullet3: "நீங்கள் உள்ளிடும் சொத்து, குடியிருப்பாளர் மற்றும் கட்டண தகவல்கள் இந்த டெமோவிற்காக மட்டுமே சேமிக்கப்படும், மூன்றாம் தரப்பினருடன் பகிரப்படாது.",
      bullet4: "அறிக்கைகள் மற்றும் நோட்புக்கில் உள்ள எண்ணிக்கைகள் நீங்கள் உள்ளிட்ட தரவிலிருந்து நேரடியாக கணக்கிடப்படுகின்றன — தணிக்கை செய்யப்படவில்லை.",
      bullet5: "வரைபடம் மற்றும் இருப்பிட அம்சங்கள் Google Maps ஆல் வழங்கப்படுகின்றன, அவை Google-இன் சொந்த விதிமுறைகளுக்கு உட்பட்டவை.",
    },
    tenantTerms: {
      intro: "ToLetBook-ஐ பயன்படுத்துவதன் மூலம் நீங்கள் இந்த விதிமுறைகளுக்கு ஒப்புக்கொள்கிறீர்கள்.",
      bullet1: "ToLetBook மூலம் செய்யப்படும் வாடகை மற்றும் டெபாசிட் கட்டணங்கள் Razorpay மூலம் செயலாக்கப்படுகின்றன. ToLetBook ஒரு வங்கி அல்ல, மேலும் Razorpay உறுதிப்படுத்துவதற்கு அப்பால் எந்த முடிவுக்கும் உத்தரவாதம் அளிக்காது.",
      bullet2: "நீங்கள் வழங்கும் தகவல்கள் சரியாக இருப்பது உங்கள் பொறுப்பு.",
      bullet3: "நீங்கள் உள்ளிடும் சொத்து, குடியிருப்பாளர் மற்றும் கட்டண தகவல்கள் இந்த டெமோவிற்காக மட்டுமே சேமிக்கப்படும், மூன்றாம் தரப்பினருடன் பகிரப்படாது.",
      bullet4: "காட்டப்படும் எண்ணிக்கைகள் நீங்கள் அல்லது உங்கள் உரிமையாளர் உள்ளிட்ட தரவிலிருந்து நேரடியாக கணக்கிடப்படுகின்றன — தணிக்கை செய்யப்படவில்லை.",
      bullet5: "வரைபடம் மற்றும் இருப்பிட அம்சங்கள் Google Maps ஆல் வழங்கப்படுகின்றன, அவை Google-இன் சொந்த விதிமுறைகளுக்கு உட்பட்டவை.",
    },
  },
};
